import React, { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import Carousel from 'react-material-ui-carousel';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

interface PlaceImagesProps {
  images?: string[];
}

/**
 * Place images carousel component with thumbnails and lightbox.
 */
export const PlaceImages: React.FC<PlaceImagesProps> = ({ images = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // <768px
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '40vh',
          width: '100%',
          bgcolor: 'grey.100',
          borderRadius: 1, // 8px (theme.spacing(1))
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No images available
        </Typography>
      </Box>
    );
  }

  const hasSingleImage = images.length === 1;

  const handleImageClick = () => {
    setLightboxIndex(0); // Always start at first image
    setLightboxOpen(true);
  };

  const handleThumbnailClick = (index: number) => {
    setCarouselIndex(index);
  };

  // Prepare lightbox slides
  const lightboxSlides = images.map((image) => ({
    src: image,
  }));

  return (
    <Box sx={{ mb: 2 }}>
      <Carousel
        animation="slide"
        navButtonsAlwaysVisible={!isMobile && !hasSingleImage}
        indicators={!hasSingleImage}
        index={carouselIndex}
        onChange={(index) => setCarouselIndex(index)}
        sx={{
          borderRadius: 1, // 8px (theme.spacing(1))
          overflow: 'hidden',
          mb: 2, // Spacing between carousel and thumbnails
        }}
      >
        {images.map((image, index) => (
          <Box
            key={index}
            onClick={handleImageClick}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '40vh',
              width: '100%',
              cursor: 'pointer',
              aspectRatio: '16/9',
            }}
          >
            <img
              src={image}
              alt={`carousel-${index}`}
              loading="lazy"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        ))}
      </Carousel>

      {/* Thumbnail strip - only show if more than one image */}
      {images.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1, // 8px gap between thumbnails
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            },
            pb: 1, // Padding bottom for scrollbar
          }}
        >
          {images.map((image, index) => (
            <Box
              key={index}
              onClick={() => handleThumbnailClick(index)}
              sx={{
                flexShrink: 0,
                width: 80,
                height: 45, // 16:9 aspect ratio (80 * 9/16 = 45)
                borderRadius: 1, // 8px
                overflow: 'hidden',
                cursor: 'pointer',
                border: carouselIndex === index ? 2 : 0,
                borderColor: 'primary.main',
                opacity: carouselIndex === index ? 1 : 0.7,
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 1,
                },
              }}
            >
              <img
                src={image}
                alt={`thumbnail-${index}`}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxSlides}
        // Keyboard navigation enabled by default (arrow keys, ESC)
        // Swipe gestures enabled by default (left/right navigate, down close)
        // Navigation buttons always visible by default
      />
    </Box>
  );
};
